import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { HiSortAscending, HiSortDescending } from 'react-icons/hi';
import EmptyPage from './EmptyPage';

function Table({ isLoading, sortKey, onSort, format, sort, data }) {
  const [sortBy, orderBy] = useMemo(() => (sort || '').split(':'), [sort]);

  const handleSort = (col) => (e) => {
    e.preventDefault();
    const switchOrder = orderBy === 'asc' ? 'desc' : 'asc';
    onSort((state) => ({
      ...state,
      [sortKey]: `${col.sortKey}:${
        sortBy !== col.sortKey ? 'asc' : switchOrder
      }`,
    }));
  };
  return (
    // Wrap this component in a div element and add 'flex flex-col' className
    // Also add fixed height for horizontal scroll
    <div
      className={cn(
        'overflow-auto flex-grow',
        !isLoading && data?.length < 1
          ? 'flex items-center justify-center'
          : '',
      )}
    >
      {isLoading || data?.length < 1 ? (
        <div className={cn(isLoading ? 'h-auto' : 'h-full')}>
          {isLoading ? (
            <div className="grid grid-cols-2 gap-3 p-3">
              <div className="animate-pulse bg-gray-50 h-8 w-full rounded" />
              <div className="animate-pulse bg-gray-50 h-8 w-full rounded" />
              <div className="col-span-2 animate-pulse bg-gray-50 h-8 w-full rounded" />
              <div className="col-span-2 animate-pulse bg-gray-50 h-8 w-full rounded" />
              <div className="col-span-2 animate-pulse bg-gray-50 h-8 w-full rounded" />
            </div>
          ) : (
            <div className="h-full">
              <EmptyPage spiel="There are no items." />
            </div>
          )}
        </div>
      ) : (
        <table className="h-auto w-full table-auto relative">
          <thead className="sticky top-0 bg-light-gray-50 h-[57px]">
            <tr>
              {React.Children.toArray(
                format.map((col) => (
                  <th
                    key={col?.id || col?.key}
                    scope="col"
                    width={col.width || 'initial'}
                  >
                    {onSort && col.sortKey ? (
                      <a
                        href="/"
                        onClick={handleSort(col)}
                        className={cn(
                          'whitespace-nowrap px-6 py-3 text-left text-xs font-medium tracking-wider uppercase flex items-center space-x-2 hover:bg-gray-100 transition',
                          sortBy === col.sortKey
                            ? 'text-primary-500'
                            : 'text-gray-500',
                        )}
                      >
                        <span>{col.label}</span>
                        {sortBy === col.sortKey && orderBy === 'desc' ? (
                          <HiSortAscending className="w-4 h-4" />
                        ) : (
                          <HiSortDescending className="w-4 h-4" />
                        )}
                      </a>
                    ) : (
                      <span
                        className={cn(
                          'p-3 text-left text-gray-500 text-sm font-bold tracking-wider uppercase flex items-center flex-shrink-0',
                          col.labelClassName || '',
                        )}
                      >
                        {col.label}
                      </span>
                    )}
                  </th>
                )),
              )}
            </tr>
          </thead>
          <tbody>
            {React.Children.toArray(
              data.map((row) => (
                <tr
                  key={JSON.stringify(row)}
                  className="group hover:bg-gray-50 transition"
                >
                  {React.Children.toArray(
                    format.map((col) => {
                      const content = (() => {
                        if (typeof col.key === 'function') return col.key(row);
                        return row[col.key];
                      })();
                      return (
                        <td
                          className={cn(
                            'px-3 py-2 text-left text-sm text-gray-700',
                            col.contentClassName || '',
                          )}
                          width={col.width || 'initial'}
                        >
                          {content}
                        </td>
                      );
                    }),
                  )}
                </tr>
              )),
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

Table.defaultProps = {
  isLoading: false,
  sortKey: 'sort',
  onSort: false,
  data: [],
  sort: '',
};

Table.propTypes = {
  onSort: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  format: PropTypes.instanceOf(Array).isRequired,
  data: PropTypes.instanceOf(Array),
  isLoading: PropTypes.bool,
  sortKey: PropTypes.string,
  sort: PropTypes.string,
};

export default Table;
